"use client";
import React from "react";
import { Link } from "react-router-dom";
import { AiChatMessage } from "@/types/ai-assistant";
import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";

interface AiMessageBubbleProps {
  message: AiChatMessage;
  onNavigate?: () => void;
}

export function AiMessageBubble({ message, onNavigate }: AiMessageBubbleProps) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="mb-3 flex justify-end">
        <div className="bg-primary text-primary-foreground max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 flex flex-col items-start">
      <div className="mb-1 ml-1 flex items-center gap-1">
        <Sparkles className="text-primary size-3" />
        <span className="text-primary text-xs font-semibold">
          AIアシスタント
        </span>
      </div>
      <div className="bg-muted max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed">
        <MarkdownContent content={message.content} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function MarkdownContent({
  content,
  onNavigate,
}: {
  content: string;
  onNavigate?: () => void;
}) {
  const lines = content.split("\n");

  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith("### ")) {
          return (
            <p key={i} className="mt-2 text-sm font-bold">
              {line.slice(4)}
            </p>
          );
        }
        if (line.startsWith("## ")) {
          return (
            <p key={i} className="mt-2 text-base font-bold">
              {line.slice(3)}
            </p>
          );
        }
        if (line.startsWith("# ")) {
          return (
            <p key={i} className="mt-2 text-lg font-bold">
              {line.slice(2)}
            </p>
          );
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return (
            <div key={i} className="flex gap-2 pl-2">
              <span>•</span>
              <span>{renderInline(line.slice(2), onNavigate)}</span>
            </div>
          );
        }
        if (/^\d+\. /.test(line)) {
          const match = line.match(/^(\d+)\. (.*)/);
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-2">
                <span className="font-semibold">{match[1]}.</span>
                <span>{renderInline(match[2], onNavigate)}</span>
              </div>
            );
          }
        }
        if (line.trim() === "") {
          return <div key={i} className="h-1" />;
        }
        return (
          <p key={i} className="text-sm">
            {renderInline(line, onNavigate)}
          </p>
        );
      })}
    </div>
  );
}

function renderInline(text: string, onNavigate?: () => void): React.ReactNode {
  const parts = text.split(
    /(\*\*.+?\*\*|`.+?`|\[[^\]]+\]\(\/(?!\/)[A-Za-z0-9/_:?.=&%#-]*\))/
  );
  return (
    <>
      {parts.map((part, i) => {
        const internalLink = part.match(
          /^\[([^\]]+)\]\((\/(?!\/)[A-Za-z0-9/_:?.=&%#-]*)\)$/
        );
        if (internalLink) {
          return (
            <Link
              key={i}
              to={internalLink[2]}
              onClick={onNavigate}
              className="text-primary font-medium underline underline-offset-2"
            >
              {internalLink[1]}
            </Link>
          );
        }
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return (
            <code
              key={i}
              className="bg-background/60 rounded px-1 py-0.5 font-mono text-xs"
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        return part;
      })}
    </>
  );
}
